import { Link } from "react-router-dom";
import { ArrowRight, Lock, FileText, Clock, PenTool, CheckCircle2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";

const FirstDraft = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        eyebrow="Stage 02 · For authors with a manuscript"
        title={
          <>
            Upload your manuscript.{" "}
            <span className="text-gold-gradient">Get immediate feedback.</span>
          </>
        }
        intro="You have written the draft. Now upload it to FirstDraft and receive structured feedback, paragraph by paragraph, based on the proven publishing principles in Robin's bestselling book. No waiting. No guessing. Just clear, actionable notes while there is still time to change the book."
      >
        <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold px-8">
          <Link to="/sign-in" className="inline-flex items-center gap-2">
            Start Your Review
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="px-8">
          <Link to="/write-your-book">Still planning the book?</Link>
        </Button>
        <Button asChild size="lg" variant="ghost" className="px-6">
          <Link to="/extend">Manuscript finished? Next stage</Link>
        </Button>
      </PageHero>

      {/* Questions */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.2fr] lg:gap-20">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="rule-gold" aria-hidden />
                <p className="eyebrow">The questions</p>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-semibold leading-[1.1] mb-6">
                The questions every author asks.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                First Draft helps you answer them before your readers do — while there is still time
                to change the book.
              </p>
            </div>

            <ol className="space-y-6">
              {[
                "Is the central idea strong enough?",
                "Does the opening earn the reader's attention?",
                "Does every chapter serve the book's promise?",
                "Will readers remember — and recommend — it?",
              ].map((q, i) => (
                <li key={q} className="flex items-baseline gap-6 border-b border-border pb-6">
                  <span className="font-serif italic text-gold text-sm tabular-nums">0{i + 1}</span>
                  <p className="font-serif text-xl md:text-2xl leading-snug">{q}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Inside the feedback */}
      <section className="py-20 md:py-28 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">Inside the feedback</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] mb-5">
              What your FirstDraft feedback actually looks like.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Each note is tied to a specific paragraph and a specific principle from Robin's bestselling
              book — so you know what to change, and why.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.35fr_1fr] lg:gap-12">
            <div className="paper-sheet rounded-sm p-7 md:p-10 relative">
              <span className="absolute inset-y-0 left-0 w-1.5 bg-gold" aria-hidden />
              <div className="flex items-center justify-between mb-7 text-[11px] font-semibold uppercase tracking-[0.2em] text-paper-foreground/55">
                <span>Chapter 03 · The Reader's Promise</span>
                <span>Page 042</span>
              </div>

              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper-foreground/55 mb-3">
                Original
              </p>
              <p className="font-serif text-[15.5px] leading-[1.85] text-paper-foreground/75">
                This book is for anyone who wants to lead better and finally get their ideas out into
                the world in a way that matters. It draws on decades of experience across industries.
              </p>

              <div className="my-7 h-px bg-paper-foreground/15" aria-hidden />

              <div className="-mx-4 rounded-sm px-4 py-4 ring-1 ring-gold/40 bg-gold/10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper-foreground/70 mb-3">
                  Suggested revision
                </p>
                <p className="font-serif text-[15.5px] leading-[1.85] text-paper-foreground">
                  This book is for the newly appointed VP who inherits a team mid-quarter and has
                  ninety days to prove the promotion was right. You will leave with a repeatable
                  operating rhythm — and a way to talk about it that your board will trust.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {[
                {
                  tone: "Refine",
                  chapter: "Ch. 3 · Reader Promise",
                  note: "The reader is still abstract. Name the specific person, the moment they are in, and the outcome they want.",
                },
                {
                  tone: "Strength",
                  chapter: "Ch. 5 · Author Voice",
                  note: "Confident sentence rhythm — hold this as the baseline voice for the chapter.",
                },
                {
                  tone: "Rework",
                  chapter: "Ch. 7 · Book Concept",
                  note: "\"Ideas that matter\" is too broad — anchor to the single outcome the book promises.",
                },
              ].map((m) => (
                <div key={m.tone} className="card-noir p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">{m.tone}</span>
                    <span className="text-[11px] text-muted-foreground">{m.chapter}</span>
                  </div>
                  <p className="text-[15px] text-muted-foreground leading-relaxed">{m.note}</p>
                </div>
              ))}

              <div className="card-noir flex items-center gap-3 p-5 text-[13px] text-muted-foreground">
                <BookOpen className="h-4 w-4 shrink-0 text-gold" aria-hidden />
                Every note references a specific chapter of the framework.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">The process</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              One upload. A clear path from first draft to finished manuscript.
            </h2>
          </div>

          <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-4 overflow-hidden rounded-lg border border-border">
            {[
              { step: "01", icon: FileText, title: "Upload your manuscript", desc: "Upload a PDF or Word draft. Chapters are parsed automatically." },
              { step: "02", icon: Clock, title: "The analysis", desc: "Your draft is analysed against Robin's framework, paragraph by paragraph." },
              { step: "03", icon: PenTool, title: "Annotated feedback", desc: "Margin notes marked strength, refine or rework, each with a reason." },
              { step: "04", icon: CheckCircle2, title: "Revise and sign off", desc: "Work through notes, tick them off, and re-check the revised draft." },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="bg-background p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-border text-gold">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-serif italic text-gold text-sm">{step}</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-20 md:py-28 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20 lg:items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="rule-gold" aria-hidden />
                <p className="eyebrow">Your work stays yours</p>
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold leading-[1.1] mb-5">
                Private by default, at every step.
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Manuscripts are the most personal thing an author hands over. We treat them that way.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Lock, t: "Private", d: "Visible only to you within your secure FirstDraft account." },
                { icon: FileText, t: "Yours", d: "You retain full rights. Export or delete your manuscript at any time." },
                { icon: Clock, t: "Never trained on", d: "Your text is never used to train public models." },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} className="card-noir p-6">
                  <Icon className="h-5 w-5 text-gold mb-4" aria-hidden />
                  <h3 className="text-base font-semibold mb-2">{t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
};

export default FirstDraft;
